import React, { ReactElement, useMemo } from 'react';
import { EventTask, useProjectTasksService, useTimezoneService } from 'nekst-api';
import {
  FormRows,
  TextInput,
  TimeInput,
  Wysiwyg,
  RelativeRow,
  DateInput,
  FormTemplate,
  AfterSubmitFunc, useFormContextRequired,
} from 'ui-builder';
import { ProjectIdContext, Separator, TimezoneDropdown } from 'features/nekst-widgets';
import ProjectDropdown from '../ProjectDropdown';
import { ProjectEventTransactionPartiesInput } from 'features/tasks-forms';
import OrganizerInput from './OrganizerInput';
import TaskCommentsListInput from '../comments/TaskCommentsListInput';

export interface BaseEventTaskFormProps {
  submitFormFunc: (data: EventTask) => Promise<EventTask>;
  afterSubmitFunc?: AfterSubmitFunc;
  data?: EventTask;
  title: string | ReactElement;
  buttons: ReactElement;
  initialData?: EventTask;
  isDisabled?: boolean | ((data: EventTask) => boolean);
  showProjectDropdown?: boolean;
}

function DueDateRow() {
  const formContext = useFormContextRequired<EventTask>();

  const timezoneService = useTimezoneService();

  const shouldDisplayTimezoneDropdown =  useMemo(() =>
    formContext.data?.dueDate?.timezone
    && formContext.data?.dueDate?.time
    && formContext.data?.dueDate?.timezone !== timezoneService.getTimezone(),
  []);

  return (
    <>
      <RelativeRow weights={[4, 3, 3, 2]}>
        <DateInput source="dueDate.date" label="Date" />
        <TimeInput
          source="dueDate.time"
          label="Start Time"
        />
        <TimeInput
          source="dueDate.endTime"
          label="End Time"
        />
      </RelativeRow>
      {shouldDisplayTimezoneDropdown && (
        <RelativeRow weights={[6, 6]}>
          <TimezoneDropdown source="dueDate.timezone" />
        </RelativeRow>
      )}
    </>

  );
}

export default function BaseEventTaskForm(props: BaseEventTaskFormProps) {
  const projectTasksService = useProjectTasksService();

  const isDisabled = props.isDisabled ?? false;

  const showProjectDropdown = props.showProjectDropdown || false;

  return (
    <ProjectIdContext projectId={props.initialData?.projectId || 0}>
      <FormTemplate<EventTask>
        title={props.title}
        submitFormFunc={props.submitFormFunc}
        afterSubmitFunc={props.afterSubmitFunc}
        buttons={props.buttons}
        isDisabled={isDisabled}
        initialData={props.initialData || props.data}
        validationSchema={projectTasksService.getEventTaskValidationSchema()}
        dependencies={[props.initialData?.projectId]}
      >
        <FormRows>
          {showProjectDropdown && (<ProjectDropdown source="projectId" />)}
          <TextInput source="name" label="Event Name" />
          <DueDateRow />
          <OrganizerInput />
          <TextInput source="location" label="Location" />
          <Separator />
          <Wysiwyg source="description" label="Event Description" />
          <Separator />
          <ProjectEventTransactionPartiesInput />
          <Separator isVisible={(data) => data.id} />
          <TaskCommentsListInput isVisible={(data) => data.id} />
        </FormRows>
      </FormTemplate>
    </ProjectIdContext>
  );
}
